import { Box, Grid, Typography, makeStyles } from '@material-ui/core';
import { useCallback, useEffect, useState } from 'react';
import SignaturePad from 'react-signature-pad-wrapper';
import LoadingSpinnerIcon from '../theme/styled-components/LoadingSpinnerIcon';
import Button from './Button';
import Modal from './Modal';

const useSignStyles = makeStyles((theme) => ({
  clearButton: {
    position: 'absolute',
    right: 20,
    top: 0,
  },
  pad: {
    backgroundColor: theme.palette.common.white,
    borderRadius: 20,
  },
  padWrapper: {
    position: 'relative',
  },
}));

interface SignProps {
  initialValue?: string;

  open: boolean;

  closeHandler: () => void;

  signHandler: (value: string) => void;
}

function Sign({ closeHandler, initialValue, open, signHandler }: SignProps) {
  const classes = useSignStyles();

  const [ref, setRef] = useState<SignaturePad | null>(null);
  const [hasChanged, setHasChanged] = useState(false);

  const clear = () => {
    ref?.clear();
    setHasChanged(false);
  };

  const save = () => {
    if (ref) {
      const newSignature = ref.toDataURL();
      // Only call signHandler if signature actually changed
      if (newSignature !== initialValue) {
        signHandler(newSignature);
      }
      closeHandler();
    }
  };

  useEffect(() => {
    if (initialValue && ref) {
      ref.fromDataURL(initialValue);
      setHasChanged(false);
    }
  }, [initialValue, ref]);

  const handleDrawEnd = useCallback(() => {
    if (ref && ref.isEmpty && !ref.isEmpty()) {
      setHasChanged(true);
    }
  }, [ref]);

  return (
    <Modal
      open={open}
      title="Create your signature"
      footer={
        <>
          <Button variant="text" onClick={closeHandler}>
            Cancel
          </Button>

          <Button onClick={save} disabled={!hasChanged}>
            Save signature
          </Button>
        </>
      }
      closeHandler={closeHandler}
    >
      <Typography variant="body1">
        Creating a signature here will allow you to quickly sign documents in
        the future. This also allows Saltus to use your signature
        electronically/physically on your behalf if necessary. You can edit your
        signature later on in the settings area if required.
      </Typography>

      <div className={classes.padWrapper}>
        <Button
          className={classes.clearButton}
          variant="secondary"
          onClick={clear}
        >
          Clear
        </Button>

        <SignaturePad
          ref={setRef}
          options={{
            penColor: 'black',
          }}
          canvasProps={{
            className: classes.pad,
            onPointerUp: handleDrawEnd as unknown as string,
            onTouchEnd: handleDrawEnd as unknown as string,
            onMouseUp: handleDrawEnd as unknown as string,
          }}
        />
      </div>
    </Modal>
  );
}

const useSignatureStyles = makeStyles((theme) => ({
  editSignature: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: 26,
    marginTop: 20,
  },
  noSignatureText: {
    fontSize: 14,
    opacity: 0.6,
    paddingLeft: 0,
    [theme.breakpoints.up('sm')]: {
      paddingLeft: 40,
    },
  },
  signature: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.backgroundNotification,
    borderRadius: 10,
    display: 'inline-flex',
    gap: 26,
    padding: '22px 26px',
  },
  signatureImage: {
    backgroundColor: theme.palette.common.white,
    borderRadius: 10,
    display: 'block',
    height: 75,
  },
}));

interface SignatureProps {
  disableEdit?: boolean;

  displayOnly?: boolean;

  initialValue?: string;

  loading?: boolean;

  submitLabel?: string;

  onSign: (value: string) => void;

  onSubmit?: () => void;
}

function Signature({
  disableEdit = false,
  displayOnly = false,
  initialValue,
  loading = false,
  submitLabel = 'Sign document',
  onSign,
  onSubmit,
}: SignatureProps) {
  const [modal, setModal] = useState(false);

  // Use initialValue directly instead of managing signature state
  const classes = useSignatureStyles();

  const closeModal = () => {
    setModal(false);
  };

  const openModal = () => {
    setModal(true);
  };

  return (
    <>
      <Box>
        {initialValue ? (
          <>
            <Box className={classes.signature}>
              {!displayOnly && (
                <Box>
                  {onSubmit && (
                    <Button
                      disabled={loading}
                      variant="primary"
                      onClick={onSubmit}
                      data-testid="signature-submit-button"
                    >
                      {submitLabel}
                      {loading && <LoadingSpinnerIcon light />}
                    </Button>
                  )}
                </Box>
              )}

              <img
                className={classes.signatureImage}
                src={initialValue}
                alt=""
                width="170"
              />
            </Box>

            {!disableEdit && (
              <Box className={classes.editSignature}>
                <Box>
                  <Typography gutterBottom variant="body2" color="textPrimary">
                    If you wish to update your current signature you can edit
                    your signature.
                  </Typography>

                  <Typography variant="body2" color="textPrimary">
                    Editing your signature will not change your signature on
                    documents you have already signed.
                  </Typography>
                </Box>

                <Button
                  variant="secondary"
                  onClick={openModal}
                  data-testid="signature-edit-button"
                >
                  Edit
                </Button>
              </Box>
            )}
          </>
        ) : (
          <Grid container direction="row" alignItems="center">
            <Grid item>
              <Button
                type="submit"
                variant="primary"
                onClick={openModal}
                data-testid="signature-create-button"
              >
                Create Signature
              </Button>
            </Grid>
            <Grid item>
              <Typography className={classes.noSignatureText} variant="caption">
                No signature added
              </Typography>
            </Grid>
          </Grid>
        )}
      </Box>

      <Sign
        open={modal}
        initialValue={initialValue}
        signHandler={onSign}
        closeHandler={closeModal}
      />
    </>
  );
}

export default Signature;
